import React, {FC} from 'react';
import styled from 'styled-components';

import {getFlatTeammates} from '../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {generateCopyValue} from '../../../../../helpers/customFieldHelpers';
import {CustomAttributeModel} from '../../../../../models/api/customFieldModel';
import {
  removeConversationCustomAttributeAction,
  updateConversationCustomAttributeAction,
} from '../../../../../store/data/conversations/customAttributesConversationsActions';
import {CustomAttributeValue} from '../../../../common/customAttributes/customAttributeValue';
import {InlineEditingCustomAttribute} from '../../../../common/inlineEditing/inlineEditingCustomAttribute';
import {ContactInformationAttribute} from '../../../../contacts/contactInformation/contactInformationAttribute';

/*
 * Props.
 */

interface ConversationCustomAttributeProps {
  conversationId: number;
  customAttribute: CustomAttributeModel;
  onValueSaved: () => void;
  onValueDiscarded: () => void;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

export const ConversationCustomAttribute: FC<ConversationCustomAttributeProps> = (props) => {
  const {conversationId, customAttribute, onValueSaved, onValueDiscarded} = props;
  const {customField} = customAttribute;

  const teammates = useAppSelector(getFlatTeammates);
  const dispatch = useAppDispatch();

  const isAddingNewCustomAttribute = customAttribute.value === '';

  return (
    <ContactInformationAttribute
      key={customField.id + customAttribute.value}
      interactionId={`conversation-custom-attribute-${customField.id}`}
      title={customField.name}
      currentAttributeValue={customAttribute.value}
      renderValue={(inlineEditingProps) => {
        if (inlineEditingProps.isEditing) {
          return (
            <InlineEditingCustomAttribute
              customAttribute={customAttribute}
              editedValue={inlineEditingProps.editedValue}
              onEdit={inlineEditingProps.onEdit}
              onEditSave={inlineEditingProps.onEditSave}
              onEditCancel={inlineEditingProps.onEditCancel}
              shouldStartInEditMode={inlineEditingProps.shouldStartInEditMode}
            />
          );
        }

        if (isAddingNewCustomAttribute) {
          return <StyledValueDiv>-</StyledValueDiv>;
        }

        return (
          <StyledValueDiv>
            <CustomAttributeValue customAttribute={customAttribute} />
          </StyledValueDiv>
        );
      }}
      copyValue={generateCopyValue(customAttribute, teammates)}
      onSave={(newValue: string) => {
        onValueSaved();

        // If the user empties the field, treat it as a delete
        if (!newValue) {
          return dispatch(removeConversationCustomAttributeAction(conversationId, customField));
        }

        return dispatch(updateConversationCustomAttributeAction(conversationId, customField, newValue));
      }}
      onDelete={() => {
        if (isAddingNewCustomAttribute) {
          onValueDiscarded();
        } else {
          dispatch(removeConversationCustomAttributeAction(conversationId, customField));
        }
      }}
      shouldStartInEditMode={isAddingNewCustomAttribute}
    />
  );
};
